import { useMemo } from "react";
import { useT } from "../i18n";
import { analyze } from "../vault/health";
import { displayHost, type DecryptedItem } from "../vault/items";
import { TypeIcon } from "./icons";
import { Clock, Star, ShieldCheck, Check, TriangleAlert, Recycle, Plus, Play } from "lucide-react";

// Vault home dashboard (UX phase 4). Shown at a vault's root (no folder/search active), above the
// root item list — so it summarizes the current vault without hiding anything. All data is derived
// from the already-decrypted in-memory item set; nothing extra is fetched. Recent/favorites lists,
// counts, a security-health summary (reusing the pure analyze()), and quick actions.
const LIST_MAX = 6;

export function Dashboard({
  orgName,
  vaultCount,
  items,
  deviceCount,
  folderCount,
  recent,
  favorites,
  desktop,
  onOpen,
  onSelect,
  onNew,
  onImport,
  onHealth,
  onQuickConnect,
}: {
  orgName: string;
  vaultCount: number;
  items: DecryptedItem[];
  deviceCount: number;
  folderCount: number;
  recent: DecryptedItem[];
  favorites: DecryptedItem[];
  desktop: boolean;
  onOpen: (it: DecryptedItem) => void;
  onSelect: (it: DecryptedItem) => void;
  onNew: () => void;
  onImport: () => void;
  onHealth: () => void;
  onQuickConnect?: () => void;
}) {
  const t = useT();
  const health = useMemo(() => analyze(items), [items]);
  const healthClean = health.weak.length === 0 && health.reused.length === 0;

  return (
    <div className="border-b border-border bg-panel2/30 px-4 py-3">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold">{orgName}</span>
        <span className="text-xs text-muted">· {t("Dashboard")}</span>
        <div className="ml-auto flex flex-wrap items-center gap-1.5 text-xs">
          <button onClick={onNew} className="flex items-center gap-1 rounded bg-accent px-2.5 py-1 font-medium text-white hover:bg-accent2"><Plus className="h-3.5 w-3.5" /> {t("New")}</button>
          <button onClick={onImport} className="rounded border border-border px-2.5 py-1 hover:border-accent">{t("Import")}</button>
          {desktop && onQuickConnect && (
            <button onClick={onQuickConnect} className="flex items-center gap-1 rounded border border-border px-2.5 py-1 hover:border-accent"><Play className="h-3.5 w-3.5" /> {t("Quick connect")}</button>
          )}
          <button onClick={onHealth} className="rounded border border-border px-2.5 py-1 hover:border-accent">{t("Health")}</button>
        </div>
      </div>

      <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Stat n={items.length} label={t("items")} />
        <Stat n={deviceCount} label={t("devices")} />
        <Stat n={folderCount} label={t("folders")} />
        <Stat n={vaultCount} label={t("vaults")} />
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <Card title={<><Clock className="h-3.5 w-3.5" /> {t("Recent")}</>}>
          <ItemList items={recent} empty={t("Nothing recent yet.")} onOpen={onOpen} onSelect={onSelect} />
        </Card>
        <Card title={<><Star className="h-3.5 w-3.5" /> {t("Favorites")}</>}>
          <ItemList items={favorites} empty={t("No favorites yet.")} onOpen={onOpen} onSelect={onSelect} />
        </Card>
        <Card title={<><ShieldCheck className="h-3.5 w-3.5" /> {t("Security health")}</>}>
          {healthClean ? (
            <div className="flex items-center justify-center gap-1.5 py-3 text-center text-xs text-ok"><Check className="h-3.5 w-3.5" /> {t("No weak or reused passwords found.")}</div>
          ) : (
            <button onClick={onHealth} className="flex w-full flex-col gap-1.5 rounded p-1 text-left text-xs hover:bg-panel2">
              <span className={`flex items-center gap-1.5 ${health.weak.length ? "text-danger" : "text-muted"}`}><TriangleAlert className="h-3.5 w-3.5" /> {health.weak.length} {t("weak")}</span>
              <span className={`flex items-center gap-1.5 ${health.reused.length ? "text-danger" : "text-muted"}`}><Recycle className="h-3.5 w-3.5" /> {health.reused.length} {t("reused")}</span>
              <span className="text-accent hover:underline">{t("Review")} →</span>
            </button>
          )}
        </Card>
      </div>
    </div>
  );
}

function Stat({ n, label }: { n: number; label: string }) {
  return (
    <div className="rounded-md border border-border bg-panel px-3 py-2">
      <div className="font-mono text-2xl font-semibold tabular-nums">{n}</div>
      <div className="eyebrow">{label}</div>
    </div>
  );
}

function Card({ title, children }: { title: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex flex-col rounded-md border border-border bg-panel p-2">
      <div className="eyebrow mb-1 flex items-center gap-1.5 px-1">{title}</div>
      {children}
    </div>
  );
}

function ItemList({
  items,
  empty,
  onOpen,
  onSelect,
}: {
  items: DecryptedItem[];
  empty: string;
  onOpen: (it: DecryptedItem) => void;
  onSelect: (it: DecryptedItem) => void;
}) {
  if (items.length === 0) return <div className="py-3 text-center text-xs text-muted">{empty}</div>;
  return (
    <div className="flex flex-col">
      {items.slice(0, LIST_MAX).map((it) => (
        <div
          key={it.id}
          onClick={() => onSelect(it)}
          onDoubleClick={() => onOpen(it)}
          className="group flex cursor-pointer items-center gap-1.5 rounded px-1 py-1 text-xs hover:bg-panel2"
        >
          <span className="shrink-0 text-muted"><TypeIcon type={it.type} className="h-3.5 w-3.5" /></span>
          <span className="min-w-0 flex-1 truncate">{it.content.name}</span>
          <span className="hidden shrink-0 truncate font-mono text-[10px] text-muted group-hover:inline">{displayHost(it.content)}</span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onOpen(it);
            }}
            title="Open"
            className="hidden shrink-0 place-items-center rounded p-0.5 text-accent hover:bg-accent/20 group-hover:grid"
          >
            <Play className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
    </div>
  );
}
